import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import ConfirmDialog from './ConfirmDialog';
import QuestionView from './QuestionView';
import LinkCommandDialog from './LinkCommandDialog';
import StepView from './StepView';
import { Course, Module } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems';
import { Lesson, MaterialQuestion, MaterialStep } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/Lessons/PathItems';
import { OperationRequest } from '@peercolab/engine';
import { useToast } from '../../providers/ToastProvider';
import { useCourses } from '../../providers/CourseProvider';
import { Session } from '../../peer-colab-model/Session';
import { BaseCommandManagement, CourseManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Command } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Commands/PathItems';

interface EditLessonMaterialProps {
  course: Course
  module: Module
  lesson: Lesson;
  patchField: <R, V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void) => Promise<void>;
}

const EditLessonMaterial: React.FC<EditLessonMaterialProps> = ({ course, module, lesson, patchField }) => {
  const { onError } = useToast()
  const { loadCourse } = useCourses()
  // State for requirements, steps, questions, commands, and minimum correct answers
  const [newRequirement, setNewRequirement] = useState("");
  // Requirement handlers
  const [editRequirementId, setEditRequirementId] = useState<string | null>(null);
  const [editRequirementText, setEditRequirementText] = useState("");
  const [editRequirementDialogOpen, setEditRequirementDialogOpen] = useState(false);
  const [baseCommands, setBaseCommands] = useState<Command[]>([]);

  useEffect(() => {
    const fetchCommands = async () => {
      onError((await Session.getClient().request(BaseCommandManagement.searchCommand({ query: '', limit: 10000, offset: 0 }))))
        .maybeOk(setBaseCommands);
    }
    fetchCommands()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddRequirement = async () => {
    if (newRequirement.trim()) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.createMaterialRequirement({ description: newRequirement.trim() })))
        .maybeOk(() => loadCourse(course.id)));
      setNewRequirement("");
    }
  };
  // Requirement delete confirmation
  const [deleteRequirementId, setDeleteRequirementId] = useState<string | null>(null);
  const [deleteRequirementDialogOpen, setDeleteRequirementDialogOpen] = useState(false);
  const handleDeleteRequirement = (id: string) => {
    setDeleteRequirementId(id);
    setDeleteRequirementDialogOpen(true);
  };
  const handleConfirmDeleteRequirement = async () => {
    if (deleteRequirementId) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.deleteMaterialRequirement(deleteRequirementId)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setDeleteRequirementId(null);
    setDeleteRequirementDialogOpen(false);
  };
  const handleCancelDeleteRequirement = () => {
    setDeleteRequirementId(null);
    setDeleteRequirementDialogOpen(false);
  };
  const handleEditRequirement = (id: string, description: string) => {
    setEditRequirementId(id);
    setEditRequirementText(description);
    setEditRequirementDialogOpen(true);
  };

  const handleEditRequirementDialogClose = () => {
    setEditRequirementDialogOpen(false);
    setEditRequirementId(null);
    setEditRequirementText("");
  };

  const handleEditRequirementDialogSave = async () => {
    if (editRequirementId !== null && editRequirementText.trim()) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.updateMaterialRequirement({ id: editRequirementId, description: editRequirementText.trim() })))
        .maybeOk(() => loadCourse(course.id)));
    }
    handleEditRequirementDialogClose();
  };

  const handleSortRequirement = async (id: string, direction: string) => {
    onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.moveMaterialRequirements([{ id, moveInstruction: { moveDirection: direction } }])))
      .maybeOk(() => loadCourse(course.id)));
  };

  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<null | MaterialStep>(null);
  const [deleteStepId, setDeleteStepId] = useState<string | null>(null);
  const [deleteStepDialogOpen, setDeleteStepDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<null | MaterialQuestion>(null);
  const [deleteQuestionId, setDeleteQuestionId] = useState<string | null>(null);
  const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
  // State for deleting a command
  const [deleteCommandId, setDeleteCommandId] = useState<string | null>(null);
  const [deleteCommandDialogOpen, setDeleteCommandDialogOpen] = useState(false);
  // Example available commands (in real app, fetch or pass as prop)
  const [linkCommandDialogOpen, setLinkCommandDialogOpen] = useState(false);
  const [minCorrect, setMinCorrect] = useState(lesson.material.minimumNumberOfCorrectAnswers.toString());

  // Handlers (stubs for now)
  const handleNewStep = () => {
    setEditingStep(null);
    setStepDialogOpen(true);
  };
  const handleEditStep = (id: string) => {
    const step = lesson.material.steps.find(s => s.step.id === id);
    if (step) {
      setEditingStep(step.step);
      setStepDialogOpen(true);
    }
  };
  const handleStepDialogClose = () => {
    setStepDialogOpen(false);
    setEditingStep(null);
  };
  const handleStepDialogSave = async (step: MaterialStep) => {
    console.log('saving step')
    if (step.id.length > 0) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.updateMaterialStep(step)))
        .maybeOk(() => loadCourse(course.id)));
    } else {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.createMaterialStep(step)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setStepDialogOpen(false);
    setEditingStep(null);
  };
  const handleDeleteStep = (id: string) => {
    setDeleteStepId(id);
    setDeleteStepDialogOpen(true);
  };
  const handleConfirmDeleteStep = async () => {
    if (deleteStepId !== null) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.deleteMaterialStep(deleteStepId)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setDeleteStepId(null);
    setDeleteStepDialogOpen(false);
  };
  const handleCancelDeleteStep = () => {
    setDeleteStepId(null);
    setDeleteStepDialogOpen(false);
  };

  const handleSortStep = async (id: string, direction: string) => {
    onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.moveMaterialSteps([{ id, moveInstruction: { moveDirection: direction } }])))
      .maybeOk(() => loadCourse(course.id)));
  };

  const handleNewQuestion = () => {
    setEditingQuestion(null);
    setQuestionDialogOpen(true);
  };
  const handleEditQuestion = (id: string) => {
    const q = lesson.material.questions.find(q => q.question.id === id);
    if (q) {
      setEditingQuestion(q.question);
      setQuestionDialogOpen(true);
    }
  };
  const handleDeleteQuestion = (id: string) => {
    setDeleteQuestionId(id);
    setDeleteQuestionDialogOpen(true);
  };
  const handleConfirmDeleteQuestion = async () => {
    if (deleteQuestionId !== null) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.deleteMaterialQuestion(deleteQuestionId)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setDeleteQuestionId(null);
    setDeleteQuestionDialogOpen(false);
  };
  const handleCancelDeleteQuestion = () => {
    setDeleteQuestionId(null);
    setDeleteQuestionDialogOpen(false);
  };
  const handleQuestionDialogClose = () => {
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
  };
  const handleQuestionDialogSave = async (q: MaterialQuestion) => {
    if (editingQuestion && editingQuestion.id) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.updateMaterialQuestion(q)))
        .maybeOk(() => loadCourse(course.id)));
    } else {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.createMaterialQuestion(q)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
  };

  const handleSortQuestion = async (id: string, direction: string) => {
    onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.moveMaterialQuestions([{ id, moveInstruction: { moveDirection: direction } }])))
      .maybeOk(() => loadCourse(course.id)));
  };

  const handleLinkCommand = () => {
    setLinkCommandDialogOpen(true);
  };
  const handleLinkCommandClose = () => {
    setLinkCommandDialogOpen(false);
  };
  const handleLinkCommandSelect = async (command: Command) => {
    // Only add if not already in list
    onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.addCommand(command.id)))
      .maybeOk(() => loadCourse(course.id)));
    setLinkCommandDialogOpen(false);
  };

  const handleDeleteCommand = (id: string) => {
    setDeleteCommandId(id);
    setDeleteCommandDialogOpen(true);
  };
  const handleConfirmDeleteCommand = async () => {
    if (deleteCommandId !== null) {
      onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.removeCommand(deleteCommandId)))
        .maybeOk(() => loadCourse(course.id)));
    }
    setDeleteCommandId(null);
    setDeleteCommandDialogOpen(false);
  };
  const handleCancelDeleteCommand = () => {
    setDeleteCommandId(null);
    setDeleteCommandDialogOpen(false);
  };

  const handleNumberOfCorrectAnswersChange = async (value: string) => {
    const newMin = parseInt(value, 10);
    onError((await Session.getLessonClient(course.id, lesson.id).request(CourseManagment.updateLessonMinimumNumberOfCorrectAnswers({
      minimumNumberOfCorrectAnswers: isNaN(newMin) ? 0 : newMin
    })))
      .maybeOk(() => {
        loadCourse(course.id);
        setMinCorrect(value);
      }));
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>


      {/* Requirements table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <TextField
            size="small"
            placeholder="Add requirement"
            value={newRequirement}
            onChange={e => setNewRequirement(e.target.value)}
            sx={{ mr: 1, width: 600 }}
          />
          <Button variant="contained" size="small" onClick={handleAddRequirement}>Add Requirement</Button>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requirement</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lesson.material.requirements.sort((a, b) => a.sortId - b.sortId).map((req, idx) => (
                <TableRow key={idx}>
                  <TableCell>{req.requirement.description}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleSortRequirement(req.requirement.id, 'up')} disabled={idx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleSortRequirement(req.requirement.id, 'down')} disabled={idx === lesson.material.requirements.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleEditRequirement(req.requirement.id, req.requirement.description)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteRequirement(req.requirement.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Edit Requirement Dialog */}
        {editRequirementDialogOpen && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.2)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper sx={{ p: 3, minWidth: 350 }}>
              <Box sx={{ mb: 2, fontWeight: 500 }}>Edit Requirement</Box>
              <TextField
                fullWidth
                value={editRequirementText}
                onChange={e => setEditRequirementText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="contained" onClick={handleEditRequirementDialogSave} disabled={!editRequirementText.trim()}>Save</Button>
                <Button variant="outlined" onClick={handleEditRequirementDialogClose}>Cancel</Button>
              </Box>
            </Paper>
          </Box>
        )}
        {/* Confirm delete requirement dialog */}
        <ConfirmDialog
          open={deleteRequirementDialogOpen}
          title="Delete Requirement?"
          content="Are you sure you want to delete this requirement? This action cannot be undone."
          onClose={handleCancelDeleteRequirement}
          onConfirm={handleConfirmDeleteRequirement}
        />
      </Box>

      {/* Steps table and New Step button */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewStep}>
            New Step
          </Button>
        </Box>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lesson.material.steps.sort((a, b) => a.sortId - b.sortId).map((step, idx) => (
                <TableRow key={step.step.id}>
                  <TableCell>{step.step.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleSortStep(step.step.id, 'up')} disabled={idx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleSortStep(step.step.id, 'down')} disabled={idx === lesson.material.steps.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleEditStep(step.step.id)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteStep(step.step.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {/* StepView dialog for new/edit step */}
              <StepView
                  open={stepDialogOpen}
                  onClose={handleStepDialogClose}
                  onSave={handleStepDialogSave}
                  initial={editingStep || undefined}
              />
              {/* Confirm delete step dialog */}
              <ConfirmDialog
                open={deleteStepDialogOpen}
                title="Delete Step?"
                content="Are you sure you want to delete this step? This action cannot be undone."
                onClose={handleCancelDeleteStep}
                onConfirm={handleConfirmDeleteStep}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Confirm delete command dialog */}
      <ConfirmDialog
        open={deleteCommandDialogOpen}
        title="Delete Command?"
        content="Are you sure you want to delete this command? This action cannot be undone."
        onClose={handleCancelDeleteCommand}
        onConfirm={handleConfirmDeleteCommand}
      />

  {/* New Question button */}
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewQuestion}>
          New Question
        </Button>
      </Box>
  {/* Questions table */}
  <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lesson.material.questions.sort((a, b) => a.sortId - b.sortId).map((q, idx) => (
              <TableRow key={q.question.id}>
                <TableCell>{q.question.question}</TableCell>
                <TableCell>{q.question.type}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleSortQuestion(q.question.id, 'up')} disabled={idx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleSortQuestion(q.question.id, 'down')} disabled={idx === lesson.material.questions.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleEditQuestion(q.question.id)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDeleteQuestion(q.question.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {/* QuestionView dialog for new/edit question */}
            <QuestionView
              open={questionDialogOpen}
              onClose={handleQuestionDialogClose}
              onSave={handleQuestionDialogSave}
              initial={editingQuestion || undefined}
            />
            {/* Confirm delete question dialog */}
            <ConfirmDialog
              open={deleteQuestionDialogOpen}
              title="Delete Question?"
              content="Are you sure you want to delete this question? This action cannot be undone."
              onClose={handleCancelDeleteQuestion}
              onConfirm={handleConfirmDeleteQuestion}
            />
          </TableBody>
        </Table>
      </TableContainer>

      {/* Minimum number of correct answers input */}
      <Box sx={{ mt: 2, mb: 4 }}>
        <TextField
          label="Minimum number of correct answers"
          type="number"
          value={minCorrect}
          onChange={e => handleNumberOfCorrectAnswersChange(e.target.value)}
          sx={{ width: 280 }}
          inputProps={{ min: 0 }}
        />
      </Box>

      {/* Link Command button above commands list */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" onClick={handleLinkCommand}>
          Link Command
        </Button>
      </Box>
      <LinkCommandDialog
        open={linkCommandDialogOpen}
        availableCommands={baseCommands.filter(cmd => !lesson.material.linkedCommands.some(id => id === cmd.id))}
        onClose={handleLinkCommandClose}
        onSelect={handleLinkCommandSelect}
      />

  {/* Commands table */}
  <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lesson.material.linkedCommands.map(cmdId => (
              <TableRow key={cmdId}>
                <TableCell>{baseCommands.find(cmd => cmd.id === cmdId)?.name}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleDeleteCommand(cmdId)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EditLessonMaterial;
